// Standard library imports
import React, { useState, useEffect } from 'react';
// External library imports
import { toast } from 'react-toastify';
import moment from 'moment';
import _ from 'lodash';
// Internal module imports
import AfrrClasses from './index.module.css';
import { DropdownComponent } from '../../../components/Inputs/Input';
import classes from '../../../styles/AllDevices.module.css';
import { useLoader, useQueryParamWithDefault } from '../../../hooks';
import { findDiffInSeconds, momentTimeFormater } from '../../../utils/timeHelper';
import DateRangePicker from '../../../components/Inputs/DateRangePicker/DateRangePicker';
import { DateParam, StringParam, useQueryParams } from 'use-query-params';
import Table from '../../../components/Table/Table';
import Typography from '../../../components/Typography/Typography';
import { EicService } from '../../../services/eicService';
import { DownloadAsExcel } from '../../../utils/downloadAsExcel';
import IconComponent from '../../../components/Icon/Icon';
import { UilImport } from '@iconscout/react-unicons';

const AREA = [
    { label: 'DK1', value: 'DK1' },
    { label: 'DK2', value: 'DK2' },
];

export const Activations = ({ cId }) => {
    const [startLoader, stopLoader] = useLoader();
    const [startDate, setStartDate] = useQueryParamWithDefault('startDate', DateParam, moment().toDate(), cId);
    const [endDate, setEndDate] = useQueryParamWithDefault('endDate', DateParam, moment().add(7, 'days').toDate(), cId);
    const [selectedArea, setSelectedArea] = useQueryParamWithDefault('area', StringParam, AREA[0].value, cId);
    const [data, setData] = useState({
        activation: [],
        count: 0,
    });
    const [paginationQuery, setPaginationQuery] = useState({
        skip: 0,
        page: 0,
        limit: 15,
    });

    useEffect(() => {
        getActivationEvents();
    }, [paginationQuery.skip, selectedArea]);

    const handlePageChange = (pageNo) => {
        setPaginationQuery((prev) => ({
            ...prev,
            skip: prev.limit * pageNo,
            page: pageNo,
        }));
    };

    const getActivationEvents = (paginated = true) => {
        const params = {
            area: selectedArea,
            startDate: moment(startDate).format('YYYY-MM-DD'),
            endDate: moment(endDate).format('YYYY-MM-DD'),
            skip: paginationQuery.skip,
            limit: paginationQuery.limit,
            paginatedData: paginated,
        };
        const handler = paginated ? handleActivationEventsSuccess : handleDownload;
        EicService.GetActivationByArea(params, startLoader, handler, handleError, stopLoader);
    };

    const handleActivationEventsSuccess = ({ data }) => {
        const processedData = _.get(data, ['data', 'data'], []).map((el) => ({
            startTime: momentTimeFormater(el.startTime).format('YYYY-MM-DD HH:mm:ss'),
            endTime: momentTimeFormater(el.endTime).format('YYYY-MM-DD HH:mm:ss'),
            maxReq: el.maxAskedPower,
            maxDel: el.maxDeliveredPower,
            duration: findDiffInSeconds(el.startTime, el.endTime),
        }));

        setData({
            activation: processedData,
            count: _.get(data, ['data', 'count'], 0),
        });
    };
    const handleDownload = ({ data }) => {
        const processedData = _.get(data, ['data', 'data'], []).map((el) => ({
            startTime: momentTimeFormater(el.startTime).format('YYYY-MM-DD HH:mm:ss'),
            endTime: momentTimeFormater(el.endTime).format('YYYY-MM-DD HH:mm:ss'),
            maxReq: el.maxAskedPower,
            maxDel: el.maxDeliveredPower,
            duration: findDiffInSeconds(el.startTime, el.endTime),
        }));

        const headers = ['Start Time (CET)', 'End Time (CET)', 'Max Requested Value (MW)', 'Max Delivered Power (MW)', 'Duration (Sec)'];

        processedData?.length > 0 && DownloadAsExcel(processedData, `aFRR Activation - ${selectedArea}`, headers);
    };

    const handleError = (err) => {
        console.log(err);
        if (err && err.response) toast.error(err.response.data.message);
    };

    return (
        <div>
            <div className={AfrrClasses.Filter}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div className={AfrrClasses.FilterContainer}>
                        <div>
                            <div className={classes.FieldControl}>
                                <label>Area</label>
                                <DropdownComponent name="area" options={AREA} defaultValue={{ value: selectedArea }} onChange={(e) => setSelectedArea(e.value)} />
                            </div>
                        </div>
                    </div>
                    <div className={AfrrClasses.FilterContainer}>
                        <div className={AfrrClasses.LeftContainer}>
                            <div className={AfrrClasses.SelecterContainer}>
                                {startDate && endDate && (
                                    <div>
                                        <DateRangePicker
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={(date) => {
                                                setStartDate(moment(date.startDate).toDate());
                                                setEndDate(moment(date.endDate).toDate());
                                            }}
                                            style={{ height: '2.5vw' }}
                                            showLabels={true}
                                        />
                                    </div>
                                )}
                            </div>

                            <div onClick={() => getActivationEvents()}>
                                <button type="submit" className="btn-primary">
                                    Submit
                                </button>
                            </div>
                            <div style={{ marginTop: '2vh' }} onClick={() => getActivationEvents(false)}>
                                <IconComponent icon={UilImport} tooltip={'Download'} size="1.5vw" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={classes.AllDevices}>
                <div className={classes.Header}>
                    <div>
                        <Typography content="Activation chips" />
                        <div className={classes.TableCount}>
                            Total Count :
                            <span>
                                <Typography size="14" content={data.count || 0} />
                            </span>
                        </div>
                    </div>
                </div>
                <div>
                    <div>
                        <Table
                            head={['Start Time (CET)', 'End Time (CET)', 'Max Requested Value (MW)', 'Max Delivered Power (MW)', 'Duration (Sec)']}
                            keys={['startTime', 'endTime', 'maxReq', 'maxDel', 'duration']}
                            data={data.activation}
                            page={paginationQuery.page}
                            Pagination={true}
                            limit={paginationQuery.limit}
                            handlePageChange={handlePageChange}
                            totalCount={data.count}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
